"use client";

// ── SIGN-UP FIELDS WITH SPECIFIC INLINE VALIDATION ───────────────────────────────────────────────
// FOUNDER-SITE-DEFECTS-20261007-001. The funnel's Continue button was `disabled` with no word about
// which field was wrong: the founder typed plausible details and a long passphrase and was left with a
// dead button. The verdict itself still lives in `@repo/ui/lib/signup-funnel` (one machine, two
// surfaces); this file only RENDERS the reasons that module now returns.
//
// WHAT THIS DOES NOT CHANGE: placeholders, `required`, the `data-auth-signup-*` hooks and the
// disabled-until-valid contract are all as they were (the port-harness funnel gates drive the form by
// placeholder and assert the button is disabled while the form is empty). It adds, per field: an
// accessible name, an `id`/`name`/`autocomplete` so password managers and browser autofill can key on
// something other than placeholder text, `aria-invalid`, and an `aria-describedby` that points at the
// specific message. Nothing here blocks paste or trims a password.

import type { ChangeEvent, CSSProperties } from "react";
import type { SignupCredentials, SignupDetails } from "@repo/ui/lib/signup-funnel";
import {
  SIGNUP_PASSWORD_RULE,
  confirmMessage,
  emailMessage,
  nameMessage,
  passwordMessage,
  passwordOk,
  phoneMessage,
} from "@repo/ui/lib/signup-funnel";

const RED = "#dc2626";
const GREY = "#828282";

/** Which fields the visitor has already left. A field that is empty AND untouched shows no error yet. */
export type SignupTouched = Partial<Record<string, boolean>>;

const errorStyle: CSSProperties = { color: RED, fontSize: "12px", margin: "0 0 8px" };

/** Show a field's message once the visitor has typed in it or left it. */
const reveal = (value: string, touched: boolean | undefined): boolean =>
  touched === true || value.length > 0;

function FieldError({ id, field, message }: { id: string; field: string; message: string | null }) {
  if (!message) return null;
  return (
    <p id={id} data-auth-signup-field-error={field} style={errorStyle}>
      {message}
    </p>
  );
}

export function SignupCredentialsFields({
  credentials,
  touched,
  onChange,
  onBlur,
  fieldStyle,
}: {
  credentials: SignupCredentials;
  touched: SignupTouched;
  onChange: (k: keyof SignupCredentials) => (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur: (k: string) => void;
  fieldStyle: CSSProperties;
}) {
  const emailError = reveal(credentials.email, touched.email) ? emailMessage(credentials.email) : null;
  const passwordError = reveal(credentials.password, touched.password)
    ? passwordMessage(credentials.password)
    : null;
  const confirmError = reveal(credentials.confirm, touched.confirm)
    ? confirmMessage(credentials.password, credentials.confirm)
    : null;
  const passwordBad = credentials.password.length > 0 && !passwordOk(credentials.password);

  return (
    <>
      <input
        id="auth-signup-email"
        name="email"
        type="email"
        autoComplete="email"
        aria-label="Email address"
        aria-invalid={emailError ? true : undefined}
        aria-describedby={emailError ? "auth-signup-email-error" : undefined}
        required
        placeholder="Email*"
        value={credentials.email}
        onChange={onChange("email")}
        onBlur={() => onBlur("email")}
        style={emailError ? { ...fieldStyle, marginBottom: "4px" } : fieldStyle}
      />
      <FieldError id="auth-signup-email-error" field="email" message={emailError} />
      <input
        id="auth-signup-password"
        name="new-password"
        type="password"
        autoComplete="new-password"
        aria-label="Set password"
        aria-invalid={passwordError ? true : undefined}
        aria-describedby={
          passwordError ? "auth-signup-password-rule auth-signup-password-error" : "auth-signup-password-rule"
        }
        required
        placeholder="Set Password*"
        value={credentials.password}
        onChange={onChange("password")}
        onBlur={() => onBlur("password")}
        style={fieldStyle}
      />
      <input
        id="auth-signup-confirm"
        name="confirm-password"
        type="password"
        autoComplete="new-password"
        aria-label="Confirm password"
        aria-invalid={confirmError ? true : undefined}
        aria-describedby={confirmError ? "auth-signup-confirm-error" : undefined}
        required
        placeholder="Confirm Password*"
        value={credentials.confirm}
        onChange={onChange("confirm")}
        onBlur={() => onBlur("confirm")}
        style={{ ...fieldStyle, marginBottom: "6px" }}
      />
      {/* The exact requirement, always on screen — not only after the visitor has already failed it. */}
      <div
        id="auth-signup-password-rule"
        data-auth-signup-password-rule
        style={{ fontSize: "10px", color: passwordBad ? RED : GREY, marginBottom: "10px" }}
      >
        {SIGNUP_PASSWORD_RULE}
      </div>
      <FieldError id="auth-signup-password-error" field="password" message={passwordError} />
      <FieldError id="auth-signup-confirm-error" field="confirm" message={confirmError} />
    </>
  );
}

export function SignupDetailsFields({
  details,
  touched,
  onChange,
  onBlur,
  fieldStyle,
}: {
  details: SignupDetails;
  touched: SignupTouched;
  onChange: (k: keyof SignupDetails) => (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur: (k: string) => void;
  fieldStyle: CSSProperties;
}) {
  const firstError = reveal(details.firstName, touched.firstName) ? nameMessage("first name", details.firstName) : null;
  const lastError = reveal(details.lastName, touched.lastName) ? nameMessage("last name", details.lastName) : null;
  const phoneError = reveal(details.phone, touched.phone) ? phoneMessage(details.phone) : null;

  return (
    <>
      <div style={{ display: "flex", gap: "10px" }}>
        <input
          id="auth-signup-given-name"
          name="given-name"
          autoComplete="given-name"
          aria-label="First name"
          aria-invalid={firstError ? true : undefined}
          aria-describedby={firstError ? "auth-signup-given-name-error" : undefined}
          required
          placeholder="First Name*"
          value={details.firstName}
          onChange={onChange("firstName")}
          onBlur={() => onBlur("firstName")}
          style={fieldStyle}
        />
        <input
          id="auth-signup-family-name"
          name="family-name"
          autoComplete="family-name"
          aria-label="Last name"
          aria-invalid={lastError ? true : undefined}
          aria-describedby={lastError ? "auth-signup-family-name-error" : undefined}
          required
          placeholder="Last Name*"
          value={details.lastName}
          onChange={onChange("lastName")}
          onBlur={() => onBlur("lastName")}
          style={fieldStyle}
        />
      </div>
      <FieldError id="auth-signup-given-name-error" field="firstName" message={firstError} />
      <FieldError id="auth-signup-family-name-error" field="lastName" message={lastError} />
      <input
        id="auth-signup-tel"
        name="tel"
        type="tel"
        autoComplete="tel"
        aria-label="Phone number"
        aria-invalid={phoneError ? true : undefined}
        aria-describedby={phoneError ? "auth-signup-tel-error" : undefined}
        required
        placeholder="Phone* (for transport notifications)"
        value={details.phone}
        onChange={onChange("phone")}
        onBlur={() => onBlur("phone")}
        style={fieldStyle}
      />
      <FieldError id="auth-signup-tel-error" field="phone" message={phoneError} />
    </>
  );
}

/**
 * The answer to "why won't Continue work?". Rendered directly above the disabled button, which points at
 * it with `aria-describedby`, so the reason is on screen for sighted visitors and attached to the control
 * for assistive tech. Absent (null) when there is nothing blocking.
 */
export function SignupBlockedReason({
  id,
  action,
  reasons,
}: {
  id: string;
  /** the button's own label, so the sentence names what is unavailable */
  action: string;
  reasons: string[];
}) {
  if (reasons.length === 0) return null;
  return (
    <div id={id} data-auth-signup-blocked style={{ margin: "0 0 10px", fontSize: "12px", color: "#4b5563" }}>
      <p style={{ margin: "0 0 4px", fontWeight: 600 }}>{action} is unavailable until:</p>
      <ul style={{ margin: 0, paddingLeft: "18px" }}>
        {reasons.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>
    </div>
  );
}
